/**
 * Course Manager - LMS
 * Manage courses, modules, and assignments
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../api/axiosClient';

function CourseManager() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterCertType, setFilterCertType] = useState('');

  const userRole = JSON.parse(localStorage.getItem('user') || '{}').role || '';
  const isCertBody = userRole === 'certification_body';

  useEffect(() => {
    fetchCourses();
  }, [filterCertType]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      const response = await axiosClient.get(`/courses/courses?${params.toString()}`);
      setCourses(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError('Failed to load courses');
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate('/courses/new');
  };

  const handleEdit = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  const handleDuplicate = async (courseId) => {
    if (!confirm('Duplicate this course?')) return;

    try {
      await axiosClient.post(`/courses/courses/${courseId}/duplicate`);
      alert('Course duplicated successfully');
      fetchCourses();
    } catch (err) {
      console.error('Error duplicating course:', err);
      alert('Failed to duplicate course');
    }
  };

  const handleArchive = async (courseId) => {
    if (!confirm('Archive this course? It will be marked as inactive.')) return;

    try {
      await axiosClient.put(`/courses/courses/${courseId}`, { is_active: false });
      alert('Course archived successfully');
      fetchCourses();
    } catch (err) {
      console.error('Error archiving course:', err);
      alert('Failed to archive course');
    }
  };

  const handleDelete = async (courseId, courseTitle) => {
    if (!confirm(`Delete "${courseTitle}"? This cannot be undone.`)) return;
    try {
      await axiosClient.delete(`/courses/courses/${courseId}`);
      fetchCourses();
    } catch (err) {
      console.error('Error deleting course:', err);
      alert(err.response?.data?.detail || 'Failed to delete course');
    }
  };

  const containerStyle = {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px'
  };

  const titleStyle = {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1a1a1a',
    margin: 0
  };

  const buttonStyle = {
    padding: '12px 24px',
    fontSize: '1rem',
    fontWeight: '600',
    background: '#d4af37',
    color: '#1a1a1a',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  };

  const filterBarStyle = {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    padding: '20px',
    background: '#fff',
    border: '2px solid #d4af37',
    borderRadius: '8px',
    alignItems: 'center',
    flexWrap: 'wrap'
  };

  const labelStyle = {
    fontWeight: '600',
    color: '#1a1a1a'
  };

  const selectStyle = {
    padding: '8px 12px',
    fontSize: '1rem',
    border: '2px solid #d4af37',
    borderRadius: '4px',
    background: '#fff',
    color: '#1a1a1a',
    fontWeight: '500'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '24px'
  };

  const theadStyle = {
    background: '#f9f9f9',
    borderBottom: '2px solid #d4af37'
  };

  const thStyle = {
    padding: '16px',
    textAlign: 'left',
    fontWeight: '700',
    color: '#1a1a1a',
    fontSize: '0.9rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const tdStyle = {
    padding: '16px',
    borderBottom: '1px solid #f0f0f0',
    color: '#333',
    fontSize: '0.95rem'
  };

  const actionButtonStyle = {
    padding: '6px 12px',
    fontSize: '0.85rem',
    fontWeight: '600',
    border: '2px solid #d4af37',
    borderRadius: '4px',
    background: '#fff',
    color: '#1a1a1a',
    cursor: 'pointer',
    marginRight: '8px'
  };

  const statusBadgeStyle = (isActive) => ({
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    background: isActive ? '#d4af37' : '#f0f0f0',
    color: isActive ? '#1a1a1a' : '#666'
  });

  const emptyStateStyle = {
    padding: '48px 24px',
    textAlign: 'center',
    background: '#fff',
    border: '2px solid #d4af37',
    borderRadius: '8px',
    color: '#666'
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>Course Manager</h1>
        </div>
        <div style={emptyStateStyle}>Loading courses...</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div>
          <h1 style={titleStyle}>Course Manager</h1>
          <p style={{ color: '#666', margin: '8px 0 0 0' }}>
            {isCertBody
              ? 'Manage your certification body\'s courses and modules'
              : 'Manage LMS courses, modules, and assignments'}
          </p>
        </div>
        <button onClick={handleCreate} style={buttonStyle}>
          + Create Course
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: '#ffebee', border: '2px solid #e74c3c', borderRadius: '4px', color: '#c62828', marginBottom: '24px' }}>
          {error}
        </div>
      )}

      <div style={filterBarStyle}>
        <div style={labelStyle}>Filters:</div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <label style={labelStyle}>
            Category:
            <select
              value={filterCertType}
              onChange={(e) => setFilterCertType(e.target.value)}
              style={{ ...selectStyle, marginLeft: '8px' }}
            >
              <option value="">All Categories</option>
              {[...new Set(courses.map(c => c.category_name).filter(Boolean))].map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {(() => {
        const filtered = filterCertType
          ? courses.filter(c => c.category_name === filterCertType)
          : courses;
        return filtered.length === 0 ? (
          <div style={emptyStateStyle}>
            <p style={{ fontSize: '1.1rem', fontWeight: '600' }}>No courses found</p>
            <p style={{ fontSize: '0.9rem', margin: '8px 0 0 0' }}>
              {filterCertType ? 'Try different filters' : 'Create your first course to get started'}
            </p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '16px', color: '#666', fontSize: '0.95rem' }}>
              Showing {filtered.length} course(s)
            </div>
            <div style={{ overflowX: 'auto', border: '2px solid #d4af37', borderRadius: '8px' }}>
              <table style={tableStyle}>
                <thead style={theadStyle}>
                  <tr>
                    <th style={thStyle}>Course Title</th>
                    <th style={thStyle}>Category</th>
                    {!isCertBody && <th style={thStyle}>Owner</th>}
                    <th style={thStyle}>Modules</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Created</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((course) => (
                  <tr key={course.id}>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <strong style={{ color: '#1a1a1a' }}>{course.title}</strong>
                        {course.is_scorm && (
                          <span style={{
                            background: '#e3f2fd', color: '#1565c0',
                            fontSize: '0.7rem', fontWeight: '700',
                            padding: '2px 8px', borderRadius: '12px',
                            textTransform: 'uppercase', letterSpacing: '0.04em',
                            flexShrink: 0,
                          }}>
                            SCORM
                          </span>
                        )}
                      </div>
                      {course.description && (
                        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                          {course.description.substring(0, 80)}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {course.category_name ? (
                        <div>
                          <span style={{ fontWeight: '600', color: '#1a1a1a' }}>{course.category_name}</span>
                          <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '2px', textTransform: 'uppercase' }}>{course.certificate_type}</div>
                        </div>
                      ) : (
                        <span style={{ textTransform: 'uppercase', fontWeight: '600', color: '#d4af37' }}>
                          {course.certificate_type}
                        </span>
                      )}
                    </td>
                    {!isCertBody && (
                      <td style={tdStyle}>
                        {course.provider_company_id ? (
                          <span style={{
                            background: '#fff3cd', color: '#856404',
                            fontSize: '0.75rem', fontWeight: '700',
                            padding: '2px 8px', borderRadius: '12px',
                            textTransform: 'uppercase', letterSpacing: '0.04em',
                          }}>
                            {course.provider_company?.name || 'Cert Body'}
                          </span>
                        ) : (
                          <span style={{
                            background: '#e8f5e9', color: '#2e7d32',
                            fontSize: '0.75rem', fontWeight: '700',
                            padding: '2px 8px', borderRadius: '12px',
                            textTransform: 'uppercase', letterSpacing: '0.04em',
                          }}>
                            Global
                          </span>
                        )}
                      </td>
                    )}
                    <td style={tdStyle}>
                      <span style={{ fontWeight: '700' }}>
                        {course.module_count || 0}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={statusBadgeStyle(course.is_active)}>
                        {course.is_active ? 'Active' : 'Archived'}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      {new Date(course.created_at).toLocaleDateString()}
                    </td>
                    <td style={tdStyle}>
                      <button onClick={() => handleEdit(course.id)} style={actionButtonStyle}>
                        Edit
                      </button>
                      <button onClick={() => handleDuplicate(course.id)} style={actionButtonStyle}>
                        Duplicate
                      </button>
                      {course.is_active && (
                        <button onClick={() => handleArchive(course.id)} style={actionButtonStyle}>
                          Archive
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(course.id, course.title)}
                        style={{ ...actionButtonStyle, borderColor: '#e74c3c', color: '#c62828' }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
        );
      })()}
    </div>
  );
}

export default CourseManager;
